import type { ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import { FileUpIcon, LibraryIcon, SparklesIcon } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCanWork } from '@/features/workspaces/canWork';

/**
 * The one add-workflow menu, opened by both the empty state's call to action
 * and the "+" header control — callers differ only in the trigger they render.
 *
 * Three sources. **Uploading a file survives** (006 FR-059): the builder is an
 * addition, not a replacement, and a team with workflow files in version control
 * would otherwise lose their way in. Creating from scratch leads, because it is
 * the path someone with no file takes and the one that needs no explanation.
 * The upload takes one workflow's YAML or a whole bundle exported from another
 * workspace (spec 204), and the item says both, or a bundle has no visible door.
 * **Install from library** (spec 104 FR-017) is a link, not a second install
 * surface: it takes the reader to the Library's Workflows tab, where the one
 * install flow lives.
 */
function AddWorkflowMenuControl({
  workspaceId,
  trigger,
  onCreateFromScratch,
  onUploadFromYaml,
}: {
  workspaceId: string;
  trigger: ReactElement<Record<string, unknown>>;
  onCreateFromScratch: () => void;
  onUploadFromYaml: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger render={trigger} />
      <DropdownMenuContent align="start" className="min-w-52">
        <DropdownMenuItem onClick={onCreateFromScratch}>
          <SparklesIcon aria-hidden="true" />
          Create from scratch
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onUploadFromYaml}>
          <FileUpIcon aria-hidden="true" />
          Upload YAML or bundle…
        </DropdownMenuItem>
        <DropdownMenuItem
          render={
            <Link
              to="/workspaces/$workspaceId/library"
              params={{ workspaceId }}
              search={{ tab: 'workflow', q: '' }}
            />
          }
        >
          <LibraryIcon aria-hidden="true" />
          Install from library
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Adding a workflow is for the workspace's members (spec 189). */
export function AddWorkflowMenu(
  props: Parameters<typeof AddWorkflowMenuControl>[0],
) {
  return useCanWork() ? <AddWorkflowMenuControl {...props} /> : null;
}
